import * as React from "react";
import { X, Loader2 } from "lucide-react";

interface DocumentTextViewerProps {
  isOpen: boolean;
  onClose: () => void;
  documentName: string;
  text: string | null | undefined;
  loading?: boolean;
}

export function DocumentTextViewer({
  isOpen,
  onClose,
  documentName,
  text,
  loading = false,
}: DocumentTextViewerProps) {
  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="relative z-10 flex max-h-[85vh] w-full max-w-3xl flex-col rounded-lg border border-line bg-surface shadow-card mx-4">
        <div className="flex items-center justify-between border-b border-line px-6 py-4">
          <h2 className="text-lg font-heading font-semibold text-ink truncate pr-4">
            {documentName}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="flex-shrink-0 rounded-sm p-1 text-content-muted hover:text-ink hover:bg-surface-100 focus:outline-none focus:ring-2 focus:ring-brand/20"
            aria-label="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-6 py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 text-content-muted animate-spin" />
            </div>
          ) : text ? (
            <pre className="whitespace-pre-wrap break-words text-sm leading-relaxed text-content font-sans">
              {text}
            </pre>
          ) : (
            <p className="text-sm text-content-muted italic">
              No se ha extraído texto de este documento.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
